import React, { useState } from 'react';
import { IncomingCallModal } from '../components/IncomingCallModal';
import { CallHistoryModal } from '../components/CallHistoryModal';
import { Phone, Video, History } from 'lucide-react';

const VideoCallDemoPage: React.FC = () => {
  const [showIncomingCall, setShowIncomingCall] = useState(false);
  const [showCallHistory, setShowCallHistory] = useState(false);
  const [callType, setCallType] = useState<'video' | 'voice'>('video');

  const handleAcceptCall = () => {
    console.log('Call accepted');
    setShowIncomingCall(false);
    // Navigate to call page would happen here
  };

  const handleRejectCall = () => {
    console.log('Call rejected');
    setShowIncomingCall(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-white mb-8 text-center">
          Video Call Demo
        </h1>

        {/* Demo Controls */}
        <div className="bg-white/10 backdrop-blur rounded-xl p-8 space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-white mb-4">Test Components</h2>
            
            {/* Call Type Selection */}
            <div className="mb-6">
              <label className="block text-white font-medium mb-2">Call Type</label>
              <div className="flex gap-3">
                <button
                  onClick={() => setCallType('video')}
                  className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
                    callType === 'video'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  <Video className="w-5 h-5" />
                  Video Call
                </button>
                <button
                  onClick={() => setCallType('voice')}
                  className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
                    callType === 'voice'
                      ? 'bg-green-600 text-white'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  <Phone className="w-5 h-5" />
                  Voice Call
                </button>
              </div>
            </div>

            {/* Incoming Call Modal Trigger */}
            <button
              onClick={() => setShowIncomingCall(true)}
              className="w-full px-6 py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg font-semibold transition-all flex items-center justify-center gap-3 shadow-xl"
            >
              {callType === 'video' ? (
                <Video className="w-6 h-6" />
              ) : (
                <Phone className="w-6 h-6" />
              )}
              Show Incoming {callType === 'video' ? 'Video' : 'Voice'} Call
            </button>
          </div>

          <div className="pt-6 border-t border-white/20">
            <button
              onClick={() => setShowCallHistory(true)}
              className="w-full px-6 py-4 bg-white/10 hover:bg-white/20 text-white rounded-lg font-semibold transition-all flex items-center justify-center gap-3"
            >
              <History className="w-6 h-6" />
              Show Call History
            </button>
          </div>
        </div>

        {/* Component Information */}
        <div className="mt-8 bg-white/10 backdrop-blur rounded-xl p-8">
          <h2 className="text-2xl font-bold text-white mb-4">Components Created</h2>
          <div className="space-y-4 text-white">
            <div className="p-4 bg-white/5 rounded-lg">
              <h3 className="font-semibold text-lg mb-2">✅ VideoCallPage</h3>
              <p className="text-gray-300 text-sm">
                Full-featured video call interface with local/remote streams, controls, and settings
              </p>
              <p className="text-blue-400 text-xs mt-2">
                Path: /call/video/:conversationId or /call/:callId
              </p>
            </div>

            <div className="p-4 bg-white/5 rounded-lg">
              <h3 className="font-semibold text-lg mb-2">✅ IncomingCallModal</h3>
              <p className="text-gray-300 text-sm">
                Modal for incoming calls with accept/reject actions and auto-timeout
              </p>
            </div>

            <div className="p-4 bg-white/5 rounded-lg">
              <h3 className="font-semibold text-lg mb-2">✅ CallHistoryModal</h3>
              <p className="text-gray-300 text-sm">
                Call history with filtering (all/missed) and quick call buttons
              </p>
            </div>

            <div className="p-4 bg-white/5 rounded-lg">
              <h3 className="font-semibold text-lg mb-2">✅ Chat Integration</h3>
              <p className="text-gray-300 text-sm">
                Video/voice call buttons added to ChatInterface header
              </p>
            </div>
          </div>
        </div>

        {/* Features List */}
        <div className="mt-8 bg-white/10 backdrop-blur rounded-xl p-8">
          <h2 className="text-2xl font-bold text-white mb-4">Features Implemented</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-white">
            {[
              'WebRTC peer connections',
              'Local/remote stream management',
              'Audio/video toggle controls',
              'Screen sharing support',
              'Call duration tracking',
              'Picture-in-picture local video',
              'Group call participant grid',
              'Connection status indicators',
              'Fullscreen mode',
              'Settings modal',
              'Incoming call notifications',
              'Call history with filters',
              'Auto-reject timeout (30s)',
              'Responsive design',
              'Gradient UI themes',
              'Smooth animations'
            ].map((feature, index) => (
              <div key={index} className="flex items-center gap-2 text-sm">
                <div className="w-2 h-2 bg-green-400 rounded-full" />
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modals */}
      <IncomingCallModal
        callerId={1}
        callerName="John Doe"
        callerAvatar="/default-avatar.png"
        callType={callType}
        onAccept={handleAcceptCall}
        onReject={handleRejectCall}
        isVisible={showIncomingCall}
      />

      <CallHistoryModal
        isOpen={showCallHistory}
        onClose={() => setShowCallHistory(false)}
        onCallUser={(userId) => {
          console.log('Calling user:', userId);
        }}
      />
    </div>
  );
};

export default VideoCallDemoPage;
